import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshStandardNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Fresnel rim highlight on `MeshStandardNodeMaterial`: view-dependent edge glow over a dark base. */
@EngineClass('FresnelRimNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Fresnel Rim',
  nodeMaterialGroup: 'TSL Node FX',
})
export class FresnelRimNodeMaterialAsset extends NodeMaterialAsset(MeshStandardNodeMaterial) {

  @property({ type: 'color', description: 'Rim highlight color' })
    rimColor = new THREE.Color(0.0, 0.5, 1.0);

  @property({ type: 'color', description: 'Base/fill color' })
    baseColor = new THREE.Color(0.02, 0.02, 0.05);

  @property({ type: 'number', min: 0.5, max: 10.0, step: 0.1, description: 'Fresnel exponent (sharpness)' })
    power = 3.0;

  @property({ type: 'number', min: 0.0, max: 2.0, step: 0.05, description: 'Emissive intensity of the rim' })
  override emissiveIntensity = 0.6;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Roughness' })
  override roughness = 0.2;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Metalness' })
  override metalness = 0.8;

  private readonly _roughnessUniform = TSL.uniform(this.roughness);
  private readonly _metalnessUniform = TSL.uniform(this.metalness);
  private readonly _powerUniform = TSL.uniform(this.power);
  private readonly _emissiveIntensityUniform = TSL.uniform(this.emissiveIntensity);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.roughness = this.clampFiniteNumber(this.roughness, 0, 1, 0.2);
    this.metalness = this.clampFiniteNumber(this.metalness, 0, 1, 0.8);
    this.power = this.clampFiniteNumber(this.power, 0.5, 10.0, 3.0);
    this.emissiveIntensity = this.clampFiniteNumber(this.emissiveIntensity, 0.0, 2.0, 0.6);
    this._roughnessUniform.value = this.roughness;
    this._metalnessUniform.value = this.metalness;
    this._powerUniform.value = this.power;
    this._emissiveIntensityUniform.value = this.emissiveIntensity;
    this.roughnessNode = this._roughnessUniform;
    this.metalnessNode = this._metalnessUniform;

    const viewDir = TSL.cameraPosition.sub(TSL.positionWorld).normalize();
    const fresnel = TSL.float(1).sub(TSL.normalWorld.dot(viewDir).saturate()).pow(this._powerUniform);
    const rimColorNode = TSL.color(this.rimColor);
    const baseColorNode = TSL.color(this.baseColor);
    this.colorNode = TSL.mix(baseColorNode, rimColorNode, fresnel);
    this.emissiveNode = rimColorNode.mul(fresnel).mul(this._emissiveIntensityUniform);
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): FresnelRimNodeMaterialAsset {
    const instance = new FresnelRimNodeMaterialAsset();
    FresnelRimNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
